import type { EntityArcMoment } from "./entityFrontmatter";
import { isJust, just, nothing, type Maybe } from "./maybe";
import { reorderExactSlotByKey } from "./reorderExactSlot";

export function newArcMomentId(): string {
  return `moment_${Math.random().toString(36).slice(2, 8)}`;
}

/** Stable sort for arc moments. `order` ascending, then id. */
export function sortArcMoments(moments: EntityArcMoment[]): EntityArcMoment[] {
  return [...moments].sort((a, b) => {
    const byOrder = a.order - b.order;
    return byOrder !== 0 ? byOrder : a.id.localeCompare(b.id);
  });
}

/** Suggest the next `order` value, spaced by 10s per the plan. */
export function nextArcOrder(moments: EntityArcMoment[]): number {
  if (moments.length === 0) return 10;
  const max = Math.max(...moments.map((moment) => moment.order));
  return max + 10;
}

/** Swap `order` with the adjacent moment in the sorted timeline. */
export function moveArcMoment(
  moments: EntityArcMoment[],
  momentId: string,
  direction: "up" | "down",
): EntityArcMoment[] {
  const sorted = sortArcMoments(moments);
  const index = sorted.findIndex((moment) => moment.id === momentId);
  if (index < 0) return moments;
  const swapIndex = direction === "up" ? index - 1 : index + 1;
  if (swapIndex < 0 || swapIndex >= sorted.length) return moments;
  return reorderArcMoment(moments, momentId, sorted[swapIndex]!.id);
}

/** Move one moment onto another's exact slot and renumber `order`. Same
 * `reorderExactSlotByKey` algorithm chapters/scenes/acts use. */
export function reorderArcMoment(
  moments: EntityArcMoment[],
  fromId: string,
  beforeId: string,
): EntityArcMoment[] {
  if (fromId === beforeId) return moments;
  const sorted = sortArcMoments(moments);
  const next = reorderExactSlotByKey(
    sorted,
    (moment) => moment.id,
    fromId,
    just(beforeId),
  );
  if (next === sorted) return moments;
  return next.map((moment, index) => ({
    ...moment,
    order: (index + 1) * 10,
  }));
}

/** #67 v1 rule: lowest `order` moment is the "next" one for peeks. */
export function nextArcMoment(
  moments: EntityArcMoment[],
): Maybe<EntityArcMoment> {
  const first = sortArcMoments(moments)[0];
  return typeof first !== "undefined" ? just(first) : nothing;
}

/**
 * #66 peek rule: contextual next arc moment relative to the scene the
 * author is reading. Unlinked moments are ignored.
 */
export function nextArcMomentForPeek(
  moments: EntityArcMoment[],
  currentScenePath: string,
  sceneOrder: readonly string[],
): Maybe<EntityArcMoment> {
  const orderIndex = new Map(
    sceneOrder.map((path, index) => [path, index] as const),
  );
  const linked = moments.filter((moment) => isJust(moment.link));
  if (linked.length === 0) return nothing;

  const sorted = [...linked].sort((a, b) => {
    const aPath = a.link.tag === "just" ? a.link.value.file.trim() : "";
    const bPath = b.link.tag === "just" ? b.link.value.file.trim() : "";
    const aIdx = orderIndex.get(aPath) ?? Number.POSITIVE_INFINITY;
    const bIdx = orderIndex.get(bPath) ?? Number.POSITIVE_INFINITY;
    const byIdx = aIdx - bIdx;
    if (byIdx !== 0) return byIdx;
    const byOrder = a.order - b.order;
    if (byOrder !== 0) return byOrder;
    return a.id.localeCompare(b.id);
  });

  const atCurrent = sorted.filter(
    (moment) =>
      moment.link.tag === "just" &&
      moment.link.value.file.trim() === currentScenePath,
  );
  const atCurrentFirst = atCurrent[0];
  if (typeof atCurrentFirst !== "undefined") return just(atCurrentFirst);

  const currentIdx = orderIndex.get(currentScenePath);
  if (typeof currentIdx !== "undefined") {
    const upcoming = sorted.find((moment) => {
      if (moment.link.tag !== "just") return false;
      const idx = orderIndex.get(moment.link.value.file.trim());
      return typeof idx !== "undefined" && idx > currentIdx;
    });
    if (typeof upcoming !== "undefined") return just(upcoming);
  }

  const last = sorted[sorted.length - 1];
  return typeof last !== "undefined" ? just(last) : nothing;
}
